import { currentUserQuery } from "@common/api/users"
import {
  workPackageSettingsQuery,
  workPackageTasksQuery,
} from "@common/api/work-packages"
import type { WorkPackageWithTasksPublic } from "@common/client"
import { useMaxWidth } from "@common/hooks/use-mobile"
import { useHasPermission } from "@common/hooks/use-permissions"
import { hasPermission } from "@common/lib/permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Outlet,
  useMatch,
  useNavigate,
} from "@tanstack/react-router"
import { Suspense } from "react"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { ColumnChips } from "@/components/layout/column-chips"
import { DETAIL_SHEET_BREAKPOINT } from "@/components/layout/detail-panel"
import { DisplayOptions } from "@/components/layout/display-options"
import { EntityTabs } from "@/components/layout/entity-tabs"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { useWidth } from "@/hooks/use-width"
import { requirePermission } from "@/lib/require-permission"
import { useTaskGroupsReadable } from "@/routes/_layout/quotes_/$quoteId/-lib/task-groups"
import { AttentionStrip } from "@/routes/_layout/quoting_/squawks/-components/attention-strip"
import { SquawkListContext } from "@/routes/_layout/quoting_/squawks/-components/squawk-list"
import {
  ensureSquawks,
  displayOf as squawkDisplayOf,
} from "@/routes/_layout/quoting_/squawks/-lib/search"
import { ActivityTab } from "./-components/activity-tab"
import { AssetTab } from "./-components/asset-tab"
import { CloseWorkOrderButton } from "./-components/close-work-order-button"
import { CustomersTab } from "./-components/customers-tab"
import { DatesTab } from "./-components/dates-tab"
import { ItemsTable, useFittedItemColumns } from "./-components/items-table"
import { NewTaskButton } from "./-components/new-task-button"
import { NextStepButton } from "./-components/next-step-button"
import { OverviewTab } from "./-components/overview-tab"
import { PartsTab } from "./-components/parts-tab"
import { NewPhaseButton } from "./-components/phase-actions"
import {
  SquawkActions,
  SquawksTab,
  useWorkOrderSquawks,
} from "./-components/squawks-tab"
import { TaskCloseStrip } from "./-components/task-close-strip"
import { itemFilterConfigs } from "./-lib/item-filters"
import {
  COLUMN_LABELS,
  columnsOffered,
  columnsSearch,
  displayOf,
  filtersOf,
  GROUPING_LABELS,
  groupingSearch,
  groupingsOf,
  hasItemFilters,
  NO_ITEM_FILTERS,
  type WorkOrderSearch,
  workOrderSearchSchema,
} from "./-lib/items"
import { usePhasesEnabled } from "./-lib/phases"
import { squawksSearchOf, workOrderSquawkParams } from "./-lib/squawks"
import { TABS, type Tab, tabLabel } from "./-lib/tabs"

const TASK_ROUTE = "/_layout/work-orders/$workOrderId/tasks/$taskId"

const SQUAWK_ROUTE = "/_layout/work-orders/$workOrderId/squawks/$squawkId"

export const Route = createFileRoute("/_layout/work-orders/$workOrderId")({
  validateSearch: (search) => workOrderSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({
    squawks: search.tab === "squawks" ? search : null,
  }),
  beforeLoad: ({ context: { queryClient } }) =>
    requirePermission(queryClient, "work_package:read", "/"),
  loader: async ({
    context: { queryClient },
    params: { workOrderId },
    deps,
  }) => {
    const [workOrder] = await Promise.all([
      queryClient.ensureQueryData(workPackageTasksQuery({ id: workOrderId })),
      queryClient.prefetchQuery(workPackageSettingsQuery()),
    ])
    const user = await queryClient.ensureQueryData(currentUserQuery())
    if (deps.squawks && hasPermission(user, "squawk:read")) {
      const search = squawksSearchOf(deps.squawks, workOrder.status)
      const params = workOrderSquawkParams(search, workOrderId)
      await ensureSquawks(
        queryClient,
        params,
        Boolean(squawkDisplayOf(search).group),
      )
    }
  },
  component: WorkOrderPage,
})

function useUpdateSearch(): (patch: Partial<WorkOrderSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function WorkOrderPage(): React.ReactElement {
  const { workOrderId } = Route.useParams()
  const search = Route.useSearch()
  const canReadSquawks = useHasPermission("squawk:read")
  const tab =
    search.tab === "squawks" && !canReadSquawks
      ? "tasks"
      : (search.tab ?? "tasks")
  const navigate = useNavigate()
  const update = useUpdateSearch()
  const { data: workOrder } = useSuspenseQuery(
    workPackageTasksQuery({ id: workOrderId }),
  )
  const phasesEnabled = usePhasesEnabled(workOrder)
  const groupsReadable = useTaskGroupsReadable()
  const departmentsReadable = useHasPermission("department:read")
  const display = displayOf(search, workOrder, phasesEnabled, groupsReadable)
  const filters = filtersOf(
    search,
    workOrder,
    groupsReadable,
    departmentsReadable,
  )
  const filtered = hasItemFilters(filters)
  const configs = itemFilterConfigs(
    workOrder,
    groupsReadable,
    departmentsReadable,
  )
  const opened = useMatch({ from: TASK_ROUTE, shouldThrow: false })
  const squawkOpened = useMatch({ from: SQUAWK_ROUTE, shouldThrow: false })
  const paned =
    (tab === "tasks" && opened) || (tab === "squawks" && squawkOpened)
  const compact = useMaxWidth(DETAIL_SHEET_BREAKPOINT)
  const [ref, width] = useWidth<HTMLDivElement>()
  const fit = useFittedItemColumns(workOrder, display.columns, width)
  const squawks = useWorkOrderSquawks(workOrder)
  const tabs = TABS.filter((value) => value !== "squawks" || canReadSquawks)

  return (
    <SquawkListContext.Provider value={squawks}>
      <ListPage
        strip={<TabStrip tab={tab} workOrder={workOrder} />}
        toolbar={
          <ListToolbar
            tabs={
              <EntityTabs
                tabs={tabs}
                tab={tab}
                label={tabLabel}
                onSelect={(picked) =>
                  navigate({
                    to: ".",
                    search: (previous) => ({
                      ...previous,
                      tab: picked === "tasks" ? undefined : picked,
                    }),
                  })
                }
              />
            }
            actions={
              <>
                {tab === "tasks" && (
                  <>
                    <FilterButton
                      active={filtered}
                      configs={configs}
                      onChange={update}
                    />
                    <DisplayOptions
                      groupings={groupingsOf(
                        workOrder,
                        phasesEnabled,
                        groupsReadable,
                      )}
                      labels={GROUPING_LABELS}
                      group={display.group}
                      subgroup={display.subgroup}
                      onChange={(next) =>
                        update(groupingSearch(next, workOrder, phasesEnabled))
                      }
                    >
                      <ColumnChips
                        options={columnsOffered(groupsReadable)}
                        labels={COLUMN_LABELS}
                        fixed={[]}
                        columns={display.columns}
                        hidden={fit.hidden}
                        onChange={(next) => update(columnsSearch(next))}
                      />
                    </DisplayOptions>
                    {phasesEnabled && <NewPhaseButton workOrder={workOrder} />}
                    <NewTaskButton workOrder={workOrder} />
                  </>
                )}
                {tab === "squawks" && <SquawkActions width={width} />}
                <NextStepButton workOrder={workOrder} />
                <CloseWorkOrderButton workOrder={workOrder} />
              </>
            }
          />
        }
        pane={paned && !compact ? "split" : "closed"}
        detail={<Outlet />}
        scrollRef={ref}
      >
        <TabContent tab={tab} workOrder={workOrder} width={width}>
          <ActiveFilterBar
            active={filtered}
            configs={configs}
            values={filters}
            onChange={update}
            onClear={() => update(NO_ITEM_FILTERS)}
          />
          <ItemsTable
            workOrder={workOrder}
            display={display}
            selectedId={opened?.params.taskId}
            width={width}
            onClear={() => update(NO_ITEM_FILTERS)}
          />
        </TabContent>
      </ListPage>
    </SquawkListContext.Provider>
  )
}

function TabStrip({
  tab,
  workOrder,
}: {
  tab: Tab
  workOrder: WorkPackageWithTasksPublic
}): React.ReactNode {
  switch (tab) {
    case "tasks":
      return <TaskCloseStrip workOrder={workOrder} />
    case "squawks":
      return (
        <Suspense>
          <AttentionStrip />
        </Suspense>
      )
    default:
      return null
  }
}

function TabContent({
  tab,
  workOrder,
  width,
  children,
}: {
  tab: Tab
  workOrder: WorkPackageWithTasksPublic
  width: number | undefined
  children: React.ReactNode
}): React.ReactNode {
  switch (tab) {
    case "overview":
      return <OverviewTab workOrder={workOrder} />
    case "tasks":
      return children
    case "squawks":
      return <SquawksTab width={width} />
    case "customers":
      return <CustomersTab workOrder={workOrder} />
    case "asset":
      return <AssetTab workOrder={workOrder} />
    case "parts":
      return <PartsTab workOrder={workOrder} />
    case "dates":
      return <DatesTab workOrder={workOrder} />
    case "activity":
      return <ActivityTab workOrderId={workOrder.id} />
  }
}
